import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { SettingService } from './setting.service';
import { Settings } from '../models/settings';
import { LoadingService } from './loading.service';
import { CustomMessageService } from './custom.message.service';
import { GridApi } from 'ag-grid-community';
import { MsalService } from '@azure/msal-angular';
import { ApiResponse } from '../models/apiResponse';

@Injectable({
  providedIn: 'root',
})
export class ApiService {  
  constructor(public http: HttpClient, public loader: LoadingService, public customMessageService: CustomMessageService, @Inject(SettingService) public settings: Settings, public msalService: MsalService) {}
 
  getData(methodName: string, httpParams: HttpParams = new HttpParams(), showLoading: boolean = true): any {
    if (showLoading) { this.loader.setMessage('Loading...'); }
     const webApiUrl = `${this.settings.webAppApiUrl}${methodName}`;
     return this.http.get(webApiUrl, { params: httpParams}).pipe(   
      map((response: any) => {      
        return this.responseHandler(response, false);
      }), 
      catchError((error) => {
        this.loader.clearMessage();
        return this.errorHandler(methodName, error, httpParams);
      }),
        finalize(() => {
          if (showLoading) { this.loader.clearMessage(); }
        })
     );
   }

   getExcelTemplate(methodName: string, httpParams: HttpParams = new HttpParams(), showLoading: boolean = true): any {
    if (showLoading) { this.loader.setMessage('Loading...'); }
     const webApiUrl = `${this.settings.webAppApiUrl}${methodName}`;
     return this.http.get(webApiUrl, { params: httpParams, responseType: 'arraybuffer'}).pipe(   
      map((response: any) => {      
        return this.responseHandler(response, false);
      }), 
      catchError((error) => {
        this.loader.clearMessage();
        return this.errorHandler(methodName, error, httpParams);
      }),
        finalize(() => {
          if (showLoading) { this.loader.clearMessage(); }
        })
     );
   }
  
   getOData(odataQuery: string, showLoading: boolean = true): any {
    if (showLoading) { this.loader.setMessage('Loading...'); }
    const webApiUrl = `${this.settings.webAppODataUrl}${odataQuery}`;
    return this.http.get(webApiUrl).pipe(   
      map((response: any) => {        
        return response;
      }), 
      catchError((error) => {
        this.loader.clearMessage();
        return this.errorHandler(odataQuery, error);
      }),
        finalize(() => {
          if (showLoading) { this.loader.clearMessage(); }
        })
     );
   }

   getRADData(odataQuery: string, showLoading: boolean = true): any {
    if (showLoading) { this.loader.setMessage('Loading...'); }
     const odataUrl = `${this.settings.rad.odataUrl}${odataQuery}`;
     return this.http.get(odataUrl).pipe(   
      map((response: any) => {        
        return response;
      }), 
      catchError((error) => {
        this.loader.clearMessage();
        return this.errorHandler(odataQuery, error);
      }),
        finalize(() => {
          if (showLoading) { this.loader.clearMessage(); }
        })
     );
   }

   getCradleOData(odataQuery: string, accessToken: string, showLoading: boolean = true): any {
    if (showLoading) { this.loader.setMessage('Loading...'); }    
    //  this.msalService.acquireTokenSilent({ scopes: [this.settings.cradle.scope] }).subscribe((cradleResponse : any) => {
        const httpHeaders = new HttpHeaders({'Authorization' : `Bearer ${accessToken}`});       
       const odataUrl = `${this.settings.cradle.odataUrl}${odataQuery}`;
       return this.http.get(odataUrl,  { headers: httpHeaders }).pipe(   
          map((response: any) => {        
            return response;
          }), 
          catchError((error) => {
            this.loader.clearMessage();
            return this.errorHandler(odataQuery, error);
          }),
            finalize(() => {
              if (showLoading) { this.loader.clearMessage(); }
            })
        );
   // });    
   }
 
   postData(methodName: string, data: any, showSuccesMessage: boolean = true, showLoading: boolean = true, shallRetry: boolean = false) :  Observable<any>{
     if(showLoading) { this.loader.setMessage('Loading...'); }
     const webApiUrl = `${this.settings.webAppApiUrl}${methodName}`;
     return this.http.post(webApiUrl, data).pipe(
      map((response: any) => {
        return this.responseHandler(response, showSuccesMessage);       
      }),
      catchError((error) => {
        if (shallRetry && error.status) {
          return throwError(() => error);
        } else {
          this.loader.clearMessage();
          return this.errorHandler(methodName, error, data);
        }
      }),  
       finalize(() => {
        if(showLoading) { this.loader.clearMessage(); }
       })
     );
   }   

   putData(methodName: string, data: any, showSuccesMessage: boolean = true, showLoading: boolean = true, headerLegend?: boolean) {
    if(showLoading) { this.loader.setMessage('Loading...'); }
    const webApiUrl = `${this.settings.webAppApiUrl}${methodName}`;
    return this.http.put(webApiUrl, data).pipe(
      map((response: any) => {
        return this.responseHandler(response, showSuccesMessage, headerLegend);
      }),
      catchError((error) => {
        this.loader.clearMessage();
        return this.errorHandler(methodName, error, data, showSuccesMessage);
      }),
      finalize(() => {
        if(showLoading) { this.loader.clearMessage(); }
      })
    );
  }

  downloadExcel(excelBlob: any) {
    this.getData("GetMIPAccessToken", new HttpParams(), false).subscribe((mipAccessToken: string) => {      
      this.downloadExcelWithMipLabels(excelBlob, mipAccessToken).subscribe();
    });
  }
  
  downloadExcelWithMipLabels(excelBlob: any, mipAccessToken: string) {
    this.loader.setMessage('Loading...');
    const webApiUrl = `${this.settings.mip.apiUrl}file/applylabel?labelId=fd642671-1056-4f23-b89a-001c5f369fbb`; 
    const formData = new FormData();
    formData.append('file', excelBlob, 'filename.xlsx'); 
    const httpHeaders = new HttpHeaders({
      'Authorization' : `Bearer ${mipAccessToken}`
    });     
    return this.http.post(webApiUrl, formData, { headers: httpHeaders, responseType: 'blob' }).pipe(
      map((response: any) => {
        if (response) { 
          const blob = new Blob([response], { type: 'application/ms-excel' });
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = window.URL.createObjectURL(blob);     
          let fileName = "PCM_" + this.customMessageService.getDateString(new Date()) + ".xlsx";
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          setTimeout(function () {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          }, 100);
       }
      }),
    catchError((error) => {
      return this.errorHandler("downloadExcelWithMipLabels", error);
    }),
    finalize(() => {
      this.loader.clearMessage();    
    })
    );
  }

  public downloadExcelWithMipLabelsAPI(agGridApi?: GridApi, additionalColumns?: string[], selected? : boolean, isExcelWithValidation? : boolean, legend?: any, headerMap?: any) {
    // agGridApi.getAllDisplayedColumns().map(p => 
    // { 
    //   let col = p.getColDef();    
    //   if(col.headerName !== "__") {
    //     columns.push({ "headerName": col.headerName , "field": col.field ? col.field : 'field', "mappedField": col.valueSetter, "objectField": col.valueParser, "objectCondition": col.valueFormatter, "objectConditionalField": col.filterValueGetter, "arrayObjectEnableValue": col.enableValue, "cellEditor": col.cellEditor, "values": col.cellEditorParams?.values});    
    //   }  
    // });   
    //const agGridData = '{"columns": ' + JSON.stringify(columns) + ', "data": ' + JSON.stringify(data) + ', "legend": ' + JSON.stringify(legend) + ', "headerMap": ' + JSON.stringify(headerMap) + ', "isExcel_CODE": '+ isExcel_CODE +'}';   
    this.loader.setMessage('Loading...');
    let data: any = []; 
    let columns: any = [];  

    if (!selected) {
      agGridApi.forEachNodeAfterFilterAndSort(function(node) {      
        if(node.data) {          
          node.data.selected = node.isSelected();  
          data.push(node.data);
        }
      });   
    } else {
      let selectedRows = agGridApi.getSelectedNodes();
      selectedRows.forEach(node => {      
        if(node.data) {
          node.data.selected = node.isSelected();
          data.push(node.data);
        }
      });   
    }

     agGridApi.getAllDisplayedColumns().map(p => 
     { 
        let col = p.getColDef();    
        if(col.headerName && col.headerName !== "__") {
          let column : any = new Object();
          column.headerName = col.headerName;
          column.field = col.field ? col.field : 'field';   
          column.mappedField = typeof col.valueSetter == 'string' ? col.valueSetter : "";
          column.objectField = typeof col.valueParser == 'string' ? col.valueParser : "";
          column.objectCondition = typeof col.valueFormatter == 'string' ? col.valueFormatter : "";
          column.objectConditionalField = typeof col.filterValueGetter == 'string' ? col.filterValueGetter : "";
          column.cellEditor = typeof col.cellEditor == 'string' ? col.cellEditor : "";        
          column.arrayObjectEnableValue = col.enableValue; 
          column.values = col.cellEditorParams?.values ?? [];      
          columns.push(column);    
        }  
      });      

      additionalColumns.forEach(col => 
      {
        let column : any = new Object();
        column.headerName = col;
        column.field = col;     
        column.mappedField = "";
        column.objectField = "";
        column.objectCondition = "";
        column.objectConditionalField = "";
        column.cellEditor = "";     
        column.values = [];      
        columns.push(column);    
      });

      let postData : any= new Object();
      postData.data = data;
      postData.columns = columns;   
      postData.isExcelWithValidation = isExcelWithValidation; 
      postData.legend = legend;
      postData.headerMap = headerMap;

      const webApiUrl = `${this.settings.webAppApiUrl}DownLoadExcelFile`;   
      const httpHeaders = new HttpHeaders({
        'Content-Type' : 'application/json'
      });          
      return this.http.post(webApiUrl, JSON.stringify(postData), { headers: httpHeaders, responseType: 'arraybuffer' }).pipe(
      catchError((error) => {
        return this.errorHandler("downloadExcelWithMipLabelsAPI", error);
      }),
      finalize(() => {
        this.loader.clearMessage();
      })
    );
  }   

  responseHandler(response: any, showSuccesMessage: boolean, headerLegend? : boolean): any {
    if (showSuccesMessage) {
      if (response.msgType) {
        let apiResponse: ApiResponse = response;
        this.customMessageService.showMessage({'severity': apiResponse.msgType, summary: apiResponse.summary, detail: apiResponse.message });  
      } else {
        this.customMessageService.showMessage({'severity': 'success', summary: 'Success', detail: headerLegend ? "Header Legend Prefrence Saved Successfully" : typeof response  === 'string' && !headerLegend ? response  : "Data Saved Successfully" });  
      }        
    }   
    return response;
  }

  errorHandler(callInfo: string, httpErrorResponse: HttpErrorResponse, data: any = null, showErrorMessage: boolean = true) {  
    if (showErrorMessage) {
      let errorTitle = typeof httpErrorResponse.error === 'string' || httpErrorResponse.error?.text ? 'Validation' : httpErrorResponse.error?.errors ? JSON.stringify(httpErrorResponse.error?.title) : httpErrorResponse.name;
      let errorMessage = typeof httpErrorResponse.error === 'string' ? httpErrorResponse.error : httpErrorResponse.error?.text ? httpErrorResponse.error?.text : httpErrorResponse.error?.errors ? JSON.stringify(httpErrorResponse.error?.errors) : httpErrorResponse.message;
      errorMessage = callInfo + " : " + errorMessage + " : " + (data !== null && typeof data === 'string' ? data : "");
      if (httpErrorResponse.status == 500) {
        this.customMessageService.showMessage({'severity': 'error', summary: errorTitle, detail: errorMessage});     
      } else{
        this.customMessageService.showMessage({'severity': 'warn', summary: errorTitle, detail: errorMessage});
      }
      return throwError(() => new Error('The Error'));
    }
    return null;
  }  

  async getFilteredAddresses(filteredText: string, showLoading: boolean = true): Promise<any> {
    if(showLoading) { this.loader.setMessage('Loading...'); }
    const response = await fetch(`https://api.geoapify.com/v1/geocode/autocomplete?text=${filteredText}&format=json&apiKey=6b0ea8fefcaa4e6b9f46772356d5fa80`);
    this.loader.clearMessage();
    return (await response.json()).results;    
   }    

   public getUserRole(groupId: string, member ?: boolean): Observable<any> {
    this.loader.setMessage('Loading...');
    const graphApiUrl = member ? `${this.settings.graphApi}?$filter=id eq '${groupId}'` : "https://graph.microsoft.com/v1.0/me";
    return this.http.get(graphApiUrl);
  }

  public getUserInfo(): any {
    let userInfourl = "https://graph.microsoft.com/v1.0/me?$select=onPremisesSamAccountName,displayName,mail,country";
    return this.http.get(userInfourl);
  }
}
